import { Box } from "@mui/material";
import { type ProjectStatus, ProjectType } from "@repo/server-api";
import ConfirmationDialog from "@repo/legacy/dialogs/ConfirmationForwardRefDialog";
import { useNotification } from "@repo/shared/components";
import { gql, useMutation } from "@apollo/client";
import { useTranslation } from "next-i18next";
import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";

import { DialogProjectChangeStatus, DialogProjectCreate, DialogProjectOffers } from "../../dialogs";
import { ProjectDataGrid } from "../../components/ProjectDataGrid";
import { ProjectListToolbar } from "../../components/ProjectListToolbar";
import { useProjectList } from "../../hooks/useProjectList";
import { createEmptyNewProject } from "../../types/ProjectTypes";

import type { ConfirmationDialogHandle } from "@repo/legacy/dialogs/ConfirmationForwardRefDialog";
import type {
	DialogProjectChangeStatusHandle,
	DialogProjectCreateShowArgsHandle,
	DialogProjectOffersHandle,
} from "../../dialogs";
import type { ProjectSummary } from "../../types/ProjectTypes";

const CREATE_OFFER_FROM_PROJECT = gql`
	mutation CreateOfferFromProject($projectId: ID!) {
		createOfferFromProject(input: { projectId: $projectId }) {
			offer {
				id
			}
			errors {
				... on DomainError {
					errorType
					message
				}
			}
		}
	}
`;

export const ProjectListPage = () => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { showNotification } = useNotification();

	const [createOfferFromProject] = useMutation(CREATE_OFFER_FROM_PROJECT);

	const confirmationRef = useRef<ConfirmationDialogHandle | null>(null);
	const dialogProjectCreateRef = useRef<DialogProjectCreateShowArgsHandle | null>(null);
	const dialogProjectOffersRef = useRef<DialogProjectOffersHandle | null>(null);
	const dialogChangeStatusRef = useRef<DialogProjectChangeStatusHandle | null>(null);

	const {
		rows,
		subRows,
		rowCount,
		isLoading,
		matchedIds,
		searchValue,
		setSearchValue,
		showInactive,
		setShowInactive,
		selectedClient,
		setSelectedClient,
		selectedStatuses,
		setSelectedStatuses,
		paginationModel,
		setPaginationModel,
		expandRow,
		deleteProject,
		refresh,
	} = useProjectList();

	const addSubProject = (parent: ProjectSummary) => {
		if (!dialogProjectCreateRef.current) return;

		const newProject = createEmptyNewProject();
		newProject.type = ProjectType.Project;
		newProject.parentProject = {
			id: parent.id,
			name: parent.name,
		};
		newProject.client =
			parent.contactInformation.client != null
				? { ...parent.contactInformation.client }
				: null;
		newProject.contractor =
			parent.contactInformation.contractor != null
				? { ...parent.contactInformation.contractor }
				: null;
		newProject.startDate = parent.startDate;
		newProject.endDate = parent.endDate;

		dialogProjectCreateRef.current.show({ project: newProject });
	};

	const handleAddProject = (type: ProjectType) => {
		const newProject = createEmptyNewProject();
		newProject.type = type;

		dialogProjectCreateRef.current?.show({ project: newProject });
	};

	const handleEditProject = (project: ProjectSummary) => {
		navigate(`/projects/${encodeURIComponent(project.id)}`);
	};

	const handleOpenOffers = (project: ProjectSummary) => {
		dialogProjectOffersRef.current?.show(project.id, project.name);
	};

	const handleDeleteProject = (id: string) => {
		confirmationRef.current?.show({
			text: t("do-you-want-to-delete-this-project"),
			onYes: () => {
				deleteProject(id);
			},
		});
	};

	const handleCreateOffer = async (project: ProjectSummary) => {
		try {
			const { data } = await createOfferFromProject({
				variables: { projectId: project.id },
			});

			const errors = data?.createOfferFromProject?.errors;
			if (errors && errors.length > 0) {
				showNotification(errors[0].message, "error");
				return;
			}

			const offerId = data?.createOfferFromProject?.offer?.id;
			if (offerId) {
				showNotification(t("offer-created-successfully"), "success");
				navigate(`/offers/draft/${encodeURIComponent(offerId)}`);
			}
		} catch {
			showNotification(t("error-occurred"), "error");
		}
	};

	const handleChangeStatus = (project: ProjectSummary) => {
		dialogChangeStatusRef.current?.show(
			project.id,
			project.name,
			project.status as ProjectStatus,
			project.isManualStatusOverride,
		);
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "max(500px, calc(100vh - 120px))",
				overflow: "hidden",
			}}
		>
			<ConfirmationDialog ref={confirmationRef} />
			<DialogProjectCreate ref={dialogProjectCreateRef} />
			<DialogProjectOffers ref={dialogProjectOffersRef} />
			<DialogProjectChangeStatus ref={dialogChangeStatusRef} onStatusChanged={refresh} />

			<h3 className="purpleHeader" style={{ margin: "0 0 16px 0", flexShrink: 0 }}>
				{t("projects")}
			</h3>

			<ProjectListToolbar
				searchValue={searchValue}
				onSearchChange={setSearchValue}
				showInactive={showInactive}
				onShowInactiveChange={setShowInactive}
				onAddProject={handleAddProject}
				selectedClient={selectedClient}
				onClientChange={setSelectedClient}
				selectedStatuses={selectedStatuses}
				onStatusChange={setSelectedStatuses}
			/>

			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					"& .MuiDataGrid-root": { height: "100% !important" },
					"& .MuiDataGrid-main": { minHeight: 0 },
				}}
			>
				<ProjectDataGrid
					rows={rows}
					subRows={subRows}
					rowCount={rowCount}
					isLoading={isLoading}
					matchedIds={matchedIds}
					showInactive={showInactive}
					paginationModel={paginationModel}
					onPaginationModelChange={setPaginationModel}
					onExpandRow={expandRow}
					onEditProject={handleEditProject}
					onDeleteProject={handleDeleteProject}
					onAddSubProject={addSubProject}
					onOpenOffers={handleOpenOffers}
				onChangeStatus={handleChangeStatus}
				onCreateOffer={handleCreateOffer}
				/>
			</Box>
		</Box>
	);
};
